/**
 * OutreachReportPanel
 *
 * Surfaces the outreach activity list with CSV/XLSX export. Reuses the
 * existing list endpoint (Promotora-scoped server-side) — a Coordinator
 * or Manager sees every row in the CBO; a Promotora sees only her own.
 *
 * For a richer per-promotora view, the data also fans into the
 * downloadXLSX helper which flattens field_data into top-level columns
 * client-side so the workbook matches what Somos's data team expects.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Download, FileSpreadsheet, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  downloadOutreachCsv,
  listOutreachActivities,
  OutreachActivity,
} from '@/services/api/outreachActivities';
import { downloadXLSX } from '@/utils/exportUtils';

const ENGAGEMENT_OPTIONS = ['Individual', 'Household', 'Workshop'];

export const OutreachReportPanel: React.FC = () => {
  const [rows, setRows] = useState<OutreachActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(() => firstOfQuarter());
  const [endDate, setEndDate] = useState<string>(() => today());
  const [engagement, setEngagement] = useState<string>('');
  // Default to "approved" — the VYC quarterly report counts only approved
  // entries (Phase 5 plan §). Reviewers can switch to "all" or other
  // statuses for ad-hoc inspection.
  const [statusFilter, setStatusFilter] = useState<string>('approved');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    listOutreachActivities({
      date_from: startDate || undefined,
      date_to: endDate || undefined,
      engagement_type: engagement || undefined,
      status: statusFilter || undefined,
      limit: 500,
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load outreach activities.');
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, engagement, statusFilter]);

  const aggregates = useMemo(() => {
    if (!rows) return null;
    const totalReached = rows.reduce(
      (sum, r) => sum + (Number(r.field_data?.outreach_num_immigrants_refugees_reached) || 0),
      0,
    );
    const byEngagement: Record<string, number> = {};
    for (const r of rows) {
      const e = String(r.field_data?.outreach_engagement_type || 'Unknown');
      byEngagement[e] = (byEngagement[e] || 0) + 1;
    }
    return { total: rows.length, totalReached, byEngagement };
  }, [rows]);

  const onCsv = () => {
    downloadOutreachCsv({
      date_from: startDate || undefined,
      date_to: endDate || undefined,
      engagement_type: engagement || undefined,
      status: statusFilter || undefined,
    }).catch((e) => setError(e?.message || 'CSV download failed.'));
  };

  const onXlsx = () => {
    if (!rows || rows.length === 0) return;
    // Flatten field_data into top-level columns so the workbook is
    // analysis-friendly (matches the server CSV's flattening rules).
    const flat = rows.map((r) => {
      const fd = r.field_data || {};
      const flatFd: Record<string, any> = {};
      for (const [k, v] of Object.entries(fd)) {
        if (Array.isArray(v) || (v && typeof v === 'object')) {
          flatFd[k] = JSON.stringify(v);
        } else {
          flatFd[k] = v;
        }
      }
      return {
        id: r.id,
        status: r.status,
        entered_by_user_id: r.assigned_to_user_id,
        created_ts: r.created_ts,
        submitted_at: r.submitted_at,
        notes: r.notes,
        ...flatFd,
      };
    });
    downloadXLSX(flat, 'outreach-activities', 'Outreach').catch((e) =>
      setError(e?.message || 'XLSX download failed.'),
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Outreach Activity Report
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Aggregate outreach entries in the selected window. Coordinators/Managers see all rows;
              Staff members see only their own.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCsv}
              disabled={!rows || rows.length === 0}
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onXlsx}
              disabled={!rows || rows.length === 0}
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              XLSX
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1 flex items-center gap-1">
              <Filter className="w-3 h-3" />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              <option value="approved">Approved (verified count)</option>
              <option value="submitted">Submitted (pending review)</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
              <option value="">All statuses</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Engagement</label>
            <select
              value={engagement}
              onChange={(e) => setEngagement(e.target.value)}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {ENGAGEMENT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">From</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">To</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">{error}</div>
        )}

        {aggregates && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Activities" value={aggregates.total} />
            <Stat label="People reached" value={aggregates.totalReached} highlight />
            {Object.entries(aggregates.byEngagement)
              .slice(0, 2)
              .map(([k, v]) => (
                <Stat key={k} label={k} value={v} />
              ))}
          </div>
        )}
        {rows && rows.length === 0 && !error && (
          <div className="text-sm text-gray-500 py-4 text-center">No entries in this range.</div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat: React.FC<{ label: string; value: number; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div
    className={`rounded-md border p-3 ${
      highlight
        ? 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
        : 'border-gray-200 dark:border-gray-700'
    }`}
  >
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-xl font-semibold ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</div>
  </div>
);

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function firstOfQuarter(): string {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
}

export default OutreachReportPanel;
